import { TextField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { Button, FormContainer, Modal, notify, Text, Flex } from '@wyz/ui';
import { CANCELLED_ORDER_STATUSES } from '@wyz/utils';
import { useForm } from 'react-hook-form';
import { useManageRedistributionOrders } from '~/purchase/api';
import { usePurchaseErrorTranslation } from '~/common/hooks';

type CancelPendingOrderModalProps = {
  onClose: () => void;
  onSuccess?: () => void;
  orderReference: string;
  customerId: string;
  tenantCode: string;
  initialComment?: string;
};

function CancelPendingOrderModal({
  customerId,
  onClose,
  onSuccess,
  tenantCode,
  orderReference,
  initialComment,
}: CancelPendingOrderModalProps) {
  const { t } = useI18n();
  const { resolveErrorKey } = usePurchaseErrorTranslation();
  const methods = useForm<{ comment?: string }>({
    defaultValues: {
      comment: initialComment,
    },
  });

  const manageRedistributionOrders = useManageRedistributionOrders({
    cancel: { enabled: true },
  });

  return (
    <Modal.Container open={true} onClose={onClose} size='lg' backdrop='static'>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.purchase.orders.pending.cancel_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Text heading='sm' weight='medium'>
          <Trans i18nKey='app.purchase.orders.pending.cancel_modal.description' />
        </Text>
        <FormContainer
          methods={methods}
          submitHandler={(form) => {
            manageRedistributionOrders.cancel.mutate(
              {
                tenantCode,
                customerId,
                orderReference,
                comment: form.comment,
                status: CANCELLED_ORDER_STATUSES.CANCELLED_BY_SUPPLIER,
              },
              {
                onSuccess: (_, variables) => {
                  notify.error(
                    <Trans
                      i18nKey={
                        'app.purchase.orders.pending.cancel_modal.success_message'
                      }
                      values={{
                        orderReference: variables.orderReference,
                      }}
                      components={[<strong key={0} />]}
                    />,
                    { id: 'toast-error-pending-order-cancelled' },
                  );
                  onClose();
                  onSuccess?.();
                },
                onError: (error) => {
                  notify.error(
                    <Trans i18nKey={resolveErrorKey(error.message)} />,
                    { id: 'toast-error-pending-order-cancel-failed' },
                  );
                },
              },
            );
          }}
        >
          <TextField
            name='comment'
            type='text'
            as='textarea'
            label={<Trans i18nKey='app.common.comment_to_customer' />}
            placeholder={t('app.common.form.add_comment')}
            rows={6}
          />
          <Flex className='space-x-2'>
            <Button
              id='cancel'
              variant='secondary'
              disabled={manageRedistributionOrders.cancel.isLoading}
              onClick={onClose}
              fullWidth
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              id='submit-cancel'
              variant='danger'
              type='submit'
              isLoading={manageRedistributionOrders.cancel.isLoading}
              fullWidth
            >
              <Trans i18nKey='app.common.button.order_cancel' />
            </Button>
          </Flex>
        </FormContainer>
      </Modal.Body>
    </Modal.Container>
  );
}

export default CancelPendingOrderModal;
